import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { navigationMenuTriggerStyle } from '@/components/ui/navigation-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useAuth } from '@/hooks/useAuth';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useMutationWithToast } from '@/hooks/useQueryWithToast';
import { useIsMobile } from '@/hooks/use-mobile';
import { useConsolidatedPermissions } from '@/hooks/useConsolidatedPermissions';
import { useCheckPermission } from '@/hooks/useUserRoles';
import { useGlobalMeetingAccess } from '@/hooks/global/useGlobalMeetingPermissions';
import { useGlobalFeatureFlag } from '@/lib/featureFlags';
import { useAppSettings } from '@/hooks/useAppSettings';
import { LogOut, User, Settings, Plus, Menu, Loader2, Bot, Trash2, Package } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SafeNavLink } from '@/components/system/SafeNavLink';
import { ProjectAddDialog } from '@/components/projects/ProjectAddDialog';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import type { ProjectStatus } from '@/types/project';
import { useOptimizedNavigation } from '@/hooks/useOptimizedNavigation';
import { useSmartPrefetch } from '@/hooks/useSmartPrefetch';

const Header = () => {
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminCheck();
  const { prefetchProjects, prefetchAdmin } = useSmartPrefetch();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const location = useLocation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { navigateFromProject, cleanupProjectQueries } = useOptimizedNavigation();
  const roadmapEnabled = useGlobalFeatureFlag('ENABLE_ROADMAP');
  const hrModuleEnabled = useGlobalFeatureFlag('ENABLE_HR_MODULE');
  
  const headerRef = useRef<HTMLElement>(null);
  
  // ✅ OPTIMIZED: Single consolidated permissions hook (replaces 9+ separate hooks)
  const consolidatedPerms = useConsolidatedPermissions();
  const permissions = consolidatedPerms.data;
  const isLoadingPermissions = consolidatedPerms.isLoading;
  
  // Derive all access from consolidated permissions
  // ✅ HARDENED: Use ONLY direct permission codes - NO fallback OR logic
  const homeAccess = isAdmin || permissions?.HOME_ACCESS;
  const projectsAccess = isAdmin || permissions?.PROJECTS_ACCESS;
  const roadmapAccess = isAdmin || permissions?.ROADMAP_ACCESS;
  const itemsAccess = isAdmin || permissions?.ITEMS_ACCESS;
  // ✅ HARDENED: Subcons uses SUBCONS_ACCESS only (no canAccessSubcons)
  const subconsAccess = isAdmin || permissions?.SUBCONS_ACCESS;
  // ✅ HARDENED: Collections uses COLLECTION_VIEW only
  const collectionsAccess = isAdmin || permissions?.COLLECTION_VIEW;
  // ✅ HARDENED: Site uses SITE_ACCESS only
  const siteAccess = isAdmin || permissions?.SITE_ACCESS;
  // ✅ HARDENED: HR uses HR_ACCESS only
  const hrAccess = isAdmin || permissions?.HR_ACCESS;
  const surveyAccess = isAdmin || permissions?.SURVEY_ACCESS;
  // ✅ AI nav visibility: DB-driven via app_settings + admin-only
  const { settings: appSettings } = useAppSettings();
  const aiProjectsAccess = isAdmin && !!appSettings.nav_ai_projects_visible;
  const aiCrmAccess = isAdmin && !!appSettings.nav_ai_crm_visible;
  const aiHrAccess = isAdmin && !!appSettings.nav_ai_hr_visible;
  
  // ✅ People Menu: requires ROADMAP_ACCESS or HR_ACCESS directly
  const showPeopleMenu = (roadmapEnabled || hrModuleEnabled) && 
                         (isAdmin || permissions?.ROADMAP_ACCESS || permissions?.HR_ACCESS);
  
  // ✅ HARDENED: Stakeholders uses STAKEHOLDERS_ACCESS only
  const canSeeStakeholders = isAdmin || permissions?.STAKEHOLDERS_ACCESS;
  // ✅ HARDENED: Maintenance uses MAINTENANCE_ACCESS only (no SITE_ACCESS fallback)
  const canSeeMaintenance = isAdmin || permissions?.MAINTENANCE_ACCESS;
  // ✅ HARDENED: Meeting uses MEETING_ACCESS only
  const { data: hasMeetingAccess } = useGlobalMeetingAccess();
  const meetingAccess = isAdmin || hasMeetingAccess;
  // ✅ Subcons By Period: quyền tách riêng, đọc primitive (không consolidated)
  const { data: canViewPeriod } = useCheckPermission('SUBCONS_GLOBAL_VIEW_PERIOD');
  
  // DEV: Debug logging for navigation permissions (throttled to prevent console flood)
  const lastPermLogRef = useRef<number>(0);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    
    // Throttle: only log once every 2 seconds max
    const now = Date.now();
    if (now - lastPermLogRef.current < 2000) return;
    lastPermLogRef.current = now;
    
    // ✅ ENHANCED DEBUG: Log all tab visibility flags
    console.info('[Header] 🔍 TAB VISIBILITY DEBUG:', {
      // Computed access flags
      subconsAccess,
      collectionsAccess,
      canSeeStakeholders,
      canSeeMaintenance,
      hrAccess,
      surveyAccess,
      
      // What triggered true (for debugging PM issue)
      stakeholdersSource: {
        isAdmin,
        'permissions.canAccessStakeholders': permissions?.canAccessStakeholders
      },
      collectionsSource: {
        isAdmin,
        'permissions.COLLECTION_VIEW': permissions?.COLLECTION_VIEW,
        'permissions.canAccessProcurement': permissions?.canAccessProcurement
      },
      subconsSource: {
        isAdmin,
        'permissions.canAccessSubcons': permissions?.canAccessSubcons,
        'permissions.SUBCONS_ACCESS': permissions?.SUBCONS_ACCESS
      },
      
      // Loading state
      isLoadingPermissions,
      
      // Original flags
      homeAccess,
      projectsAccess,
      roadmapAccess,
      showPeopleMenu,
      isMobile
    });
  }, [homeAccess, projectsAccess, roadmapAccess, showPeopleMenu, isAdmin, isMobile, subconsAccess, collectionsAccess, canSeeStakeholders, canSeeMaintenance, hrAccess, surveyAccess, permissions, isLoadingPermissions]);

  // Set header height CSS variable for PinContextMenu clamping
  useEffect(() => {
    if (!headerRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        document.documentElement.style.setProperty('--header-h', `${height}px`);
      }
    });
    
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);
  
  // Feature flag for strict permission checks (default: false for non-regression)
  const ENABLE_STRICT_PERMISSION_CHECK = import.meta.env.VITE_STRICT_PERMISSION_CHECK === 'true';
  
  // Feature flags
  const ENABLE_STAKEHOLDERS = import.meta.env.VITE_ENABLE_STAKEHOLDERS_PAGE !== 'false';
  const ENABLE_MAINTENANCE_PAGE = import.meta.env.VITE_ENABLE_MAINTENANCE_PAGE === 'true';
  const ENABLE_SERVICE_TABS = import.meta.env.VITE_ENABLE_SERVICE_TABS === 'true';
  const ENABLE_SUBCONS_PAGE = import.meta.env.VITE_ENABLE_SUBCONS_PAGE !== 'false';
  const ENABLE_COLLECTIONS_PAGE = import.meta.env.VITE_ENABLE_COLLECTIONS_PAGE !== 'false';

  // Check if we're on the projects page
  const isProjectsPage = location.pathname === '/projects';

  // Add project mutation
  const addProjectMutation = useMutationWithToast({
    mutationFn: async (projectData: { 
      project_name: string; 
      image_file: File | null;
      status?: ProjectStatus;
          project_type?: string;
          project_group?: string;
          company?: string;
    }) => {
      if (!user?.id) {
        throw new Error('User must be authenticated to create projects');
      }

      let imageUrl = null;
      
      // If there's an image file, upload it first
      if (projectData.image_file) {
        const formData = new FormData();
        formData.append('file', projectData.image_file);
        
        const { data: uploadResponse, error: uploadError } = await supabase.functions.invoke('upload-project-image', {
          body: formData
        });
        
        if (uploadError) {
          throw new Error(`Failed to upload image: ${uploadError.message}`);
        }
        
        imageUrl = uploadResponse.imageUrl;
      }
      
      // Create the project
      const { data, error } = await supabase
        .from('projects')
        .insert({
          project_name: projectData.project_name,
          image_url: imageUrl,
          user_id: user.id,
          status: projectData.status || 'tender',
          project_type: projectData.project_type || 'infrastructure',
          project_group: projectData.project_group || null,
          company: projectData.company || null
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setShowAddDialog(false);
      // Invalidate all project-related queries for real-time updates
      queryClient.invalidateQueries({ queryKey: ['projects-optimized'] });
      queryClient.invalidateQueries({ queryKey: ['projects-count'] });
    },
    successMessage: "Đã tạo dự án mới",
    errorMessage: "Không thể tạo dự án"
  });

  const getUserInitials = (name: string | undefined, email: string | undefined) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };


  return (
    <header ref={headerRef} data-app-chrome="header" className="relative z-[100] isolate pointer-events-auto border-b shadow-[0_1px_3px_0_rgba(0,0,0,0.1)] bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center justify-between overflow-hidden">
        <div className="flex items-center space-x-6">
          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              {/* Loading Skeleton - Show when permissions loading and not admin */}
              {isLoadingPermissions && !isAdmin && !permissions && (
                <nav className="flex items-center space-x-1">
                  <div className="px-3 py-2 text-sm text-muted-foreground/50 animate-pulse rounded-md bg-muted/30">
                    Home
                  </div>
                  <div className="px-3 py-2 text-sm text-muted-foreground/50 animate-pulse rounded-md bg-muted/30">
                    Projects
                  </div>
                  <div className="px-3 py-2 text-sm text-muted-foreground/50 animate-pulse rounded-md bg-muted/30">
                    Library
                  </div>
                </nav>
              )}

              {/* Actual Navigation - Show when permissions loaded OR admin */}
              {(permissions || isAdmin) && (
              <nav className="flex items-center space-x-0.5 xl:space-x-1 overflow-x-auto max-w-[calc(100vw-280px)] scrollbar-hide">
              {homeAccess && (
                <SafeNavLink 
                  to="/"
                  className={navigationMenuTriggerStyle()}
                >
                  Home
                  {isLoadingPermissions && !isAdmin && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
                </SafeNavLink>
              )}
              {projectsAccess && (
                <SafeNavLink 
                  to="/projects"
                  className={navigationMenuTriggerStyle()}
                  onMouseEnter={() => prefetchProjects({ delay: 300 })}
                >
                  Projects
                  {isLoadingPermissions && !isAdmin && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
                </SafeNavLink>
              )}
              {meetingAccess && (
                <SafeNavLink 
                  to="/meetings"
                  className={navigationMenuTriggerStyle()}
                >
                  Meeting
                  {isLoadingPermissions && !isAdmin && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
                </SafeNavLink>
              )}
              {itemsAccess && (
                <SafeNavLink 
                  to="/items"
                  className={navigationMenuTriggerStyle()}
                >
                  Build
                  {isLoadingPermissions && !isAdmin && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
                </SafeNavLink>
              )}
              {siteAccess && (
                <SafeNavLink 
                  to="/site"
                  className={navigationMenuTriggerStyle()}
                >
                  Sites
                </SafeNavLink>
              )}
              {ENABLE_STAKEHOLDERS && canSeeStakeholders && (
                <SafeNavLink 
                  to="/stakeholders"
                  className={navigationMenuTriggerStyle()}
                  aria-disabled={isLoadingPermissions && !isAdmin}
                >
                  Stakeholders
                  {isLoadingPermissions && !isAdmin && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
                </SafeNavLink>
              )}
              {ENABLE_SUBCONS_PAGE && subconsAccess && (
                <SafeNavLink 
                  to="/subcons"
                  className={navigationMenuTriggerStyle()}
                >
                  Subcons
                </SafeNavLink>
              )}
              {ENABLE_SUBCONS_PAGE && (canViewPeriod || isAdmin) && (
                <SafeNavLink
                  to="/subcons-period"
                  className={navigationMenuTriggerStyle()}
                >
                  RegPay
                </SafeNavLink>
              )}
              {ENABLE_COLLECTIONS_PAGE && collectionsAccess && (
                <SafeNavLink 
                  to="/collections"
                  className={navigationMenuTriggerStyle()}
                >
                  Collections
                  {isLoadingPermissions && !isAdmin && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
                </SafeNavLink>
              )}
              {canSeeMaintenance && (
                <SafeNavLink 
                  to="/maintenance"
                  className={navigationMenuTriggerStyle()}
                >
                  {ENABLE_SERVICE_TABS ? 'Service' : 'Maintenance'}
                </SafeNavLink>
              )}
              {aiProjectsAccess && (
                <SafeNavLink 
                  to="/ai-projects"
                  className={navigationMenuTriggerStyle()}
                >
                  <Bot className="h-4 w-4 mr-1" />
                  AI Projects
                </SafeNavLink>
              )}
              {aiCrmAccess && (
                <SafeNavLink 
                  to="/ai-stakeholders"
                  className={navigationMenuTriggerStyle()}
                >
                  <Bot className="h-4 w-4 mr-1" />
                  AI CRM
                </SafeNavLink>
              )}
              {aiHrAccess && (
                <SafeNavLink 
                  to="/ai-hr"
                  className={navigationMenuTriggerStyle()}
                >
                  <Bot className="h-4 w-4 mr-1" />
                  AI HR
                </SafeNavLink>
              )}
            </nav>
              )}
            </>
          )}

          {/* Mobile Menu */}
          {isMobile && (
            <>
              <Sheet open={showMobileMenu} onOpenChange={setShowMobileMenu}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
              <SheetContent side="left" className="w-64">
                <nav className="flex flex-col space-y-4 mt-6">
                  {homeAccess && (
                    <SafeNavLink 
                      to="/" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span>Home</span>
                    </SafeNavLink>
                  )}
                  {projectsAccess && (
                    <SafeNavLink 
                      to="/projects" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                      onTouchStart={() => prefetchProjects({ delay: 150 })}
                    >
                      <span>Projects</span>
                    </SafeNavLink>
                  )}
                  {meetingAccess && (
                    <SafeNavLink 
                      to="/meetings" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span>Meeting</span>
                    </SafeNavLink>
                  )}
                  {itemsAccess && (
                    <SafeNavLink 
                      to="/items" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span>Build</span>
                    </SafeNavLink>
                  )}
                  {siteAccess && (
                    <SafeNavLink 
                      to="/site" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span>Sites</span>
                    </SafeNavLink>
                  )}
                  {ENABLE_STAKEHOLDERS && canSeeStakeholders && (
                    <SafeNavLink 
                      to="/stakeholders" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      Stakeholders
                    </SafeNavLink>
                  )}
                  {ENABLE_SUBCONS_PAGE && subconsAccess && (
                    <SafeNavLink 
                      to="/subcons" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span>Subcons</span>
                    </SafeNavLink>
                  )}
                  {ENABLE_SUBCONS_PAGE && (canViewPeriod || isAdmin) && (
                    <SafeNavLink
                      to="/subcons-period"
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span>RegPay</span>
                    </SafeNavLink>
                  )}
                  {ENABLE_COLLECTIONS_PAGE && collectionsAccess && (
                    <SafeNavLink 
                      to="/collections" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span>Collections</span>
                    </SafeNavLink>
                  )}
                  {canSeeMaintenance && (
                    <SafeNavLink 
                      to="/maintenance" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      {ENABLE_SERVICE_TABS ? 'Service' : 'Maintenance'}
                    </SafeNavLink>
                  )}
                  {aiProjectsAccess && (
                    <SafeNavLink 
                      to="/ai-projects" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <Bot className="h-4 w-4 mr-2" />
                      <span>AI Projects</span>
                    </SafeNavLink>
                  )}
                  {aiCrmAccess && (
                    <SafeNavLink 
                      to="/ai-stakeholders" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <Bot className="h-4 w-4 mr-2" />
                      <span>AI CRM</span>
                    </SafeNavLink>
                  )}
                  {aiHrAccess && (
                    <SafeNavLink 
                      to="/ai-hr" 
                      className="flex items-center py-2 px-3 rounded-md hover:bg-accent"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <Bot className="h-4 w-4 mr-2" />
                      <span>AI HR</span>
                    </SafeNavLink>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
            
            </>
          )}
        </div>

        <div className="flex items-center space-x-4">
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.user_metadata?.avatar_url} alt={user.user_metadata?.full_name || user.email} />
                    <AvatarFallback>
                      {getUserInitials(user.user_metadata?.full_name, user.email)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              {/* 
                Removed forceMount - DropdownMenu doesn't need it (simple animation).
                If other components need forceMount for complex presence animation,
                ensure their overlays have data-[state=closed]:pointer-events-none
              */}
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user.user_metadata?.full_name || 'Người dùng'}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <SafeNavLink to="/profile/settings" className="flex items-center">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile Settings</span>
                  </SafeNavLink>
                </DropdownMenuItem>
                {/* ✅ FIX: Khảo sát menu chỉ hiện nếu có SURVEY_ACCESS */}
                {surveyAccess && (
                  <DropdownMenuItem asChild>
                    <SafeNavLink to="/surveys" className="flex items-center">
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Khảo sát</span>
                    </SafeNavLink>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <SafeNavLink to="/trash" className="flex items-center">
                    <Trash2 className="mr-2 h-4 w-4" />
                    <span>Thùng rác</span>
                  </SafeNavLink>
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <SafeNavLink 
                      to="/admin"
                      onMouseEnter={() => prefetchAdmin({ delay: 300 })}
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Admin Panel</span>
                    </SafeNavLink>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Đăng xuất</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" size="sm" asChild>
              <Link to="/auth">Đăng nhập</Link>
            </Button>
          )}
        </div>
      </div>

      {/* Project Add Dialog */}
      <ProjectAddDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSubmit={addProjectMutation.mutate}
        isLoading={addProjectMutation.isPending}
      />
    </header>
  );
};

export default Header;